import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { HashRouter, Routes, Route, Outlet } from 'react-router-dom'
import { normaliseTenantEntryPath } from '@/lib/tenantEntryPath'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import { Toaster } from '@/components/ui/sonner'
import { AuthProvider } from '@/hooks/useAuth'
import { RequireAuth, RequirePermission, RequireRole, TenantEntry } from '@/components/auth'
import { ErrorBoundary } from '@/components/common'
import { BudgetProvider } from '@/contexts/BudgetContext'
import { SidebarProvider } from '@/contexts/SidebarContext'
import {
  DashboardPage,
  RunListPage,
  RunDetailPage,
  CreateRunPage,
  OrchestratePage,
  SessionListPage,
  SessionDetailPage,
  ProjectListPage,
  ProjectDetailPage,
  CreateProjectPage,
  ChatbotPage,
  TokenStatsPage,
  AdoptionStatsPage,
  ModelPricingPage,
  PromptsPage,
  RepositoryAccessPage,
  ServiceCatalogPage,
  KnowledgeSearchPage,
  GlossaryPage,
  KnowledgeSourcesPage,
  ContextInspectorPage,
  ErrorPage,
  NotFoundPage,
} from '@/pages'
import { UserManagementPage } from '@/pages/admin/UserManagementPage'
import { BudgetManagementPage } from '@/pages/admin/BudgetManagementPage'
import './index.css'

/** The authenticated half of the app. A layout route so its children stay ordinary top-level routes. */
function RequireAuthLayout() {
  return (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  )
}

// Before the router mounts: `/t/{slug}` is a real path, but the app routes on the hash, so the path
// has to become its hash equivalent or a tenant-scoped sign-in link silently starts a generic one.
normaliseTenantEntryPath()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      {/* The query client wraps the auth provider, not the other way round: the session is now a
          TanStack Query at a fixed key, so the provider that owns it has to be inside the client. */}
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SidebarProvider>
          <BudgetProvider>
            <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <Routes>
                {/* Outside the auth gate on purpose: this route's job is to *start* a sign-in, so putting
                    it behind the gate that prompts for one would loop. */}
                <Route path="/t/:slug" element={<TenantEntry />} />

                {/* A layout route rather than a descendant <Routes>: every child below resolves against
                    the router root, with no splat prefix to reason about. */}
                <Route element={<RequireAuthLayout />}>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/runs" element={<RunListPage />} />
                <Route path="/runs/new" element={<CreateRunPage />} />
                <Route path="/runs/:id" element={<RunDetailPage />} />
                <Route path="/orchestrate" element={<OrchestratePage />} />
                <Route path="/orchestrate/:sessionId" element={<OrchestratePage />} />
                <Route path="/sessions" element={<SessionListPage />} />
                <Route path="/sessions/:sessionId" element={<SessionDetailPage />} />
                <Route path="/sessions/:sessionId/context" element={<ContextInspectorPage />} />
                <Route path="/projects" element={<ProjectListPage />} />
                <Route path="/projects/new" element={<CreateProjectPage />} />
                <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
                <Route path="/projects/:projectId/edit" element={<CreateProjectPage />} />
                <Route path="/service-catalog" element={<ServiceCatalogPage />} />
                <Route path="/knowledge/search" element={<KnowledgeSearchPage />} />
                <Route path="/knowledge/glossary" element={<GlossaryPage />} />
                <Route path="/knowledge/sources" element={<KnowledgeSourcesPage />} />
                <Route path="/assistant" element={<ChatbotPage />} />
                <Route path="/stats/tokens" element={<TokenStatsPage />} />
                <Route path="/stats/adoption" element={<AdoptionStatsPage />} />
                <Route path="/configuration/model-pricing" element={<ModelPricingPage />} />
                <Route path="/configuration/prompts" element={
                  <RequireRole roles={['super_admin']}>
                    <PromptsPage />
                  </RequireRole>
                } />
                <Route path="/configuration/repository-access" element={
                  <RequirePermission permission="config:view" fallback={<NotFoundPage />}>
                    <RepositoryAccessPage />
                  </RequirePermission>
                } />
                <Route path="/configuration/users" element={
                  <RequireRole roles={['super_admin']}>
                    <UserManagementPage />
                  </RequireRole>
                } />
                <Route path="/configuration/budget" element={
                  <RequireRole roles={['super_admin']}>
                    <BudgetManagementPage />
                  </RequireRole>
                } />
                <Route path="/error" element={<ErrorPage />} />
                <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Routes>
            </HashRouter>
            <Toaster />
          </BudgetProvider>
          </SidebarProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
)
